import React from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormControl,
  FormLabel,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  useToast,
  Badge,
  Alert,
  AlertIcon,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
} from "@chakra-ui/react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { ArrowBackIcon, DownloadIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

const GET_ALL_TENANTS = gql`
  query GetAllTenants {
    tenants {
      id
      name
      domain
      status
      subscriptionTier
      moduleConfig {
        moduleId
        version
        enabled
      }
      branding {
        siteName
      }
    }
  }
`;

const DEPLOY_MODULE_UPDATE = gql`
  mutation DeployModuleUpdate($moduleId: String!, $version: String!, $tenantIds: [ID!]) {
    deployModuleUpdate(moduleId: $moduleId, version: $version, tenantIds: $tenantIds)
  }
`;

const GET_AVAILABLE_MODULES = gql`
  query GetAvailableModules {
    availableModules {
      id
      name
      description
      icon
      version
      requiredTier
    }
  }
`;

const DeploymentManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const bg = getColor("background.primary");
  const cardBg = getComponent("card", "bg");
  const cardBorder = getComponent("card", "border");
  const buttonBg = getComponent("button", "bg");
  const buttonHoverBg = getComponent("button", "hoverBg");
  const textColor = getColor("text.primary");
  const mutedTextColor = getColor("text.muted");

  const [selectedModule, setSelectedModule] = React.useState("");
  const [selectedVersion, setSelectedVersion] = React.useState("");
  const [selectedTenants, setSelectedTenants] = React.useState<string[]>([]);
  const [isDeploying, setIsDeploying] = React.useState(false);
  const [deploymentProgress, setDeploymentProgress] = React.useState(0);

  const { loading, error, data, refetch } = useQuery(GET_ALL_TENANTS);
  const { data: modulesData, loading: modulesLoading, error: modulesError } = useQuery(GET_AVAILABLE_MODULES);
  const [deployUpdate] = useMutation(DEPLOY_MODULE_UPDATE);
  
  const tenants = data?.tenants || [];

  // Early return if there are errors
  if (error || modulesError) {
    return (
      <Box minH="100vh" bg={bg}>
        <NavbarWithCallToAction />
        <Container maxW="7xl" py={12}>
          <Alert status="error">
            <AlertIcon />
            Error loading data: {error?.message || modulesError?.message}
          </Alert>
        </Container>
        <FooterWithFourColumns />
      </Box>
    );
  }

  const handleDeploy = async () => {
    if (!selectedModule || !selectedVersion || selectedTenants.length === 0) {
      toast({
        title: "Missing Information",
        description: "Please select a module, version, and at least one tenant",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsDeploying(true);
    setDeploymentProgress(0);

    try {
      // Simulate deployment progress
      const progressInterval = setInterval(() => {
        setDeploymentProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await deployUpdate({
        variables: {
          moduleId: selectedModule,
          version: selectedVersion,
          tenantIds: selectedTenants,
        },
      });

      clearInterval(progressInterval);
      setDeploymentProgress(100);

      toast({
        title: "Deployment Successful",
        description: `${selectedModule} v${selectedVersion} deployed to ${selectedTenants.length} tenant(s)`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Reset form
      setSelectedModule("");
      setSelectedVersion("");
      setSelectedTenants([]);
      setDeploymentProgress(0);

      // Refresh data
      refetch();
    } catch (error) {
      toast({
        title: "Deployment Failed",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeploying(false);
    }
  };

  const getTenantsWithModule = (moduleId: string) => {
    return tenants.filter((tenant: any) =>
      tenant.moduleConfig.some((m: any) => m.moduleId === moduleId && m.enabled)
    );
  };

  const selectAllTenantsWithModule = (moduleId: string) => {
    const tenantsWithModule = getTenantsWithModule(moduleId);
    setSelectedTenants(tenantsWithModule.map((t: any) => t.id));
  };

  if (loading || modulesLoading) return <Text>Loading...</Text>;

  return (
    <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={12} flex="1">
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <HStack justify="space-between">
            <VStack align="start" spacing={2}>
              <HStack>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/admin/tenants")}
                  leftIcon={<ArrowBackIcon />}
                >
                  Back to Tenants
                </Button>
              </HStack>
              <Heading size="lg">🚀 Deployment Manager</Heading>
              <Text color="gray.600">Deploy module updates across multiple client sites</Text>
            </VStack>
          </HStack>

          <HStack spacing={8} align="start">
            {/* Deployment Form */}
            <Card flex={1}>
              <CardHeader>
                <Heading size="md">Deploy Module Update</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={6} align="stretch">
                  {/* Module Selection */}
                  <FormControl>
                    <FormLabel
                      color={getComponent("form", "labelColor")}
                      fontWeight="500"
                      fontSize="sm"
                      mb={2}
                    >
                      Select Module
                    </FormLabel>
                    <Select
                      placeholder="Choose a module to update"
                      value={selectedModule}
                      onChange={(e) => {
                        setSelectedModule(e.target.value);
                        // Auto-select the latest version
                        const selectedModuleData = modulesData?.availableModules?.find((m: any) => m.id === e.target.value);
                        if (selectedModuleData) {
                          setSelectedVersion(selectedModuleData.version);
                        } else {
                          setSelectedVersion("");
                        }
                        setSelectedTenants([]);
                      }}
                      bg={getComponent("form", "fieldBg")}
                      border="1px"
                      borderColor={getComponent("form", "fieldBorder")}
                      borderRadius="lg"
                      boxShadow={getComponent("form", "fieldShadow")}
                      _focus={{
                        borderColor: getComponent("form", "fieldBorderFocus"),
                        boxShadow: getComponent("form", "fieldShadowFocus"),
                        outline: "none"
                      }}
                      _hover={{
                        borderColor: getColor("border.medium")
                      }}
                      size="lg"
                      fontFamily={brandConfig.fonts.body}
                    >
                      {modulesData?.availableModules?.map((module: any) => (
                        <option key={module.id} value={module.id}>
                          {module.icon} {module.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Version Selection */}
                  {selectedModule && (
                    <FormControl>
                      <FormLabel
                        color={getComponent("form", "labelColor")}
                        fontWeight="500"
                        fontSize="sm"
                        mb={2}
                      >
                        Select Version
                      </FormLabel>
                      <Select
                        placeholder="Choose version"
                        value={selectedVersion}
                        onChange={(e) => setSelectedVersion(e.target.value)}
                        bg={getComponent("form", "fieldBg")}
                        border="1px"
                        borderColor={getComponent("form", "fieldBorder")}
                        borderRadius="lg"
                        boxShadow={getComponent("form", "fieldShadow")}
                        _focus={{
                          borderColor: getComponent("form", "fieldBorderFocus"),
                          boxShadow: getComponent("form", "fieldShadowFocus"),
                          outline: "none"
                        }}
                        _hover={{
                          borderColor: getColor("border.medium")
                        }}
                        size="lg"
                        fontFamily={brandConfig.fonts.body}
                      >
                        {(() => {
                          const selectedModuleData = modulesData?.availableModules?.find((m: any) => m.id === selectedModule);
                          if (!selectedModuleData) return null;
                          return (
                            <option key={selectedModuleData.version} value={selectedModuleData.version}>
                              v{selectedModuleData.version} (latest)
                            </option>
                          );
                        })()}
                      </Select>
                    </FormControl>
                  )}

                  {/* Tenant Selection */}
                  {selectedModule && (
                    <FormControl>
                      <HStack justify="space-between">
                        <FormLabel
                          color={getComponent("form", "labelColor")}
                          fontWeight="500"
                          fontSize="sm"
                          mb={2}
                        >
                          Select Tenants
                        </FormLabel>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => selectAllTenantsWithModule(selectedModule)}
                        >
                          Select All with {selectedModule}
                        </Button>
                      </HStack>
                      <Box maxHeight="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md" p={4}>
                        <CheckboxGroup
                          value={selectedTenants}
                          onChange={(values) => setSelectedTenants(values as string[])}
                        >
                          <Stack spacing={3}>
                            {tenants.map((tenant: any) => {
                              const hasModule = tenant.moduleConfig.some((m: any) =>
                                m.moduleId === selectedModule && m.enabled
                              );
                              const currentVersion = tenant.moduleConfig.find((m: any) =>
                                m.moduleId === selectedModule
                              )?.version;

                              return (
                                <HStack key={tenant.id} justify="space-between" w="full">
                                  <Checkbox value={tenant.id} isDisabled={!hasModule}>
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="bold">
                                        {tenant.branding?.siteName || tenant.name}
                                      </Text>
                                      <Text fontSize="sm" color="gray.500">
                                        {tenant.domain}
                                      </Text>
                                    </VStack>
                                  </Checkbox>
                                  <VStack align="end" spacing={0}>
                                    {hasModule ? (
                                      <Badge colorScheme="green">v{currentVersion}</Badge>
                                    ) : (
                                      <Badge colorScheme="gray">Not Installed</Badge>
                                    )}
                                  </VStack>
                                </HStack>
                              );
                            })}
                          </Stack>
                        </CheckboxGroup>
                      </Box>
                    </FormControl>
                  )}

                  {/* Deployment Status */}
                  {isDeploying && (
                    <Alert status="info">
                      <AlertIcon />
                      <VStack align="start" spacing={2} w="full">
                        <Text>Deploying {selectedModule} v{selectedVersion}...</Text>
                        <Progress value={deploymentProgress} size="sm" colorScheme="blue" w="full" />
                      </VStack>
                    </Alert>
                  )}

                  {/* Deploy Button */}
                  <Button
                    colorScheme="blue"
                    size="lg"
                    onClick={handleDeploy}
                    isLoading={isDeploying}
                    loadingText="Deploying..."
                    leftIcon={<DownloadIcon />}
                    isDisabled={!selectedModule || !selectedVersion || selectedTenants.length === 0}
                  >
                    Deploy to {selectedTenants.length} Tenant{selectedTenants.length !== 1 ? "s" : ""}
                  </Button>
                </VStack>
              </CardBody>
            </Card>

            {/* Deployment Summary */}
            <Card flex={1}>
              <CardHeader>
                <Heading size="md">Module Overview</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing={4} align="stretch">
                  {modulesData?.availableModules?.map((module: any) => {
                    const tenantsWithModule = getTenantsWithModule(module.id);
                    const versions = new Set(
                      tenants
                        .flatMap((t: any) => t.moduleConfig)
                        .filter((m: any) => m.moduleId === module.id && m.enabled)
                        .map((m: any) => m.version)
                    );

                    return (
                      <Box key={module.id} p={4} border="1px" borderColor="gray.200" borderRadius="md">
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Text fontSize="lg">{module.icon}</Text>
                            <Text fontWeight="bold">{module.name}</Text>
                          </HStack>
                          <Text fontSize="sm" color="gray.600">
                            {tenantsWithModule.length} tenants using this module
                          </Text>
                          <Badge variant="outline" colorScheme="blue">
                            Latest: v{module.version}
                          </Badge>
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
              </CardBody>
            </Card>
          </HStack>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </Box>
  );
};

export default DeploymentManager; 